// FICHA DEL CLIENTE completa, como la «Mantención del Maestro de entidades» de Random (pedido de
// Rodrigo, 2026-10-13): Identificación · Comercial · Direcciones · Contactos y correos ·
// Cuentas bancarias · Observaciones. Cada parte se edita con su permiso (después se decide qué puede tocar cada perfil
// comercial); lo sensible —lista, vendedor, condición, crédito, bloqueo— sigue por «Editar /
// solicitar cambio», que aplica o deja pendiente según la política de cada campo.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '../api/cliente';
import { claves, useComunas } from '../api/consultas';
import type { FichaEntidad, ListaPrecio, Vendedor } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fechaHora, NOMBRE_CONDICION, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorBanco } from '../comun/bancos';
import { NOMBRE_TIPO_CUENTA, type CuentaEntidad } from '../tesoreria/datosPagos';
import { Asignaciones, Credito } from './FichaComercial';
import { InformacionGeneral, type TipoEntidad, type ValoresGenerales } from './InformacionGeneral';

export type Seccion =
  | 'identificacion'
  | 'comercial'
  | 'asignaciones'
  | 'credito'
  | 'direcciones'
  | 'contactos'
  | 'cuentas'
  | 'observaciones';
type Direccion = FichaEntidad['direcciones'][number];
type Contacto = FichaEntidad['contactos'][number];
type Medio = FichaEntidad['medios'][number];

const AREAS = ['comercial', 'cobranza', 'compras', 'despacho', 'dte', 'gerencia', 'otro'] as const;
const PROPOSITOS = ['comercial', 'dte', 'cobranza', 'compras', 'despacho', 'general'] as const;
const TIPOS_MEDIO = ['correo', 'telefono', 'whatsapp', 'web'] as const;
const NOMBRE_PROPOSITO: Record<string, string> = {
  comercial: 'Comercial',
  dte: 'Factura electrónica (DTE)',
  cobranza: 'Cobranza',
  compras: 'Compras',
  despacho: 'Despacho',
  general: 'General',
};

interface Observacion {
  id: string;
  texto: string;
  origen: string;
  autor: string | null;
  registradoEn: string;
}

/** Una escritura de la ficha: al terminar refresca la ficha (y lo que cuelga del cliente). */
function useGuardar(empresaId: string, entidadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      metodo,
      ruta,
      cuerpo,
    }: {
      metodo: 'post' | 'patch';
      ruta: string;
      cuerpo: object;
    }) => api[metodo](ruta, { empresaId, ...cuerpo }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: claves.cliente(empresaId, entidadId) }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'observaciones', entidadId] }),
      ]),
  });
}

export function FichaMaestro({
  empresaId,
  ficha,
  vendedores,
  listas,
  alSolicitarCambio,
  modo = 'cliente',
  alta,
  seccionInicial = 'identificacion',
}: {
  empresaId: string;
  /** null = cliente NUEVO: la misma ficha, abierta en las secciones que trae `alta`. */
  ficha: FichaEntidad | null;
  vendedores: Vendedor[];
  listas: ListaPrecio[];
  alSolicitarCambio?: (() => void) | undefined;
  /** Desde Compras la ficha es del PROVEEDOR: sin lista, vendedor, crédito ni visitas de venta. */
  modo?: 'cliente' | 'proveedor';
  /** Cliente nuevo: lo que se llena de cada sección al crearlo (lo arma ClienteNuevo). */
  alta?: Partial<Record<Seccion, ReactNode>>;
  seccionInicial?: Seccion;
}) {
  const [seccion, setSeccion] = useState<Seccion>(seccionInicial);
  const rel = ficha?.empresas.find((e) => e.empresa_id === empresaId) ?? null;
  const proveedor = modo === 'proveedor';
  const activas = ficha ? ficha.direcciones.filter((d) => d.activa).length : 0;
  // Cada sección con lo que contiene, para que se entienda sin abrirla.
  // Cliente: las pestañas de la Mantención del Maestro de Random (Rodrigo, 2026-10-24).
  const secciones: Array<[Seccion, string, string]> = [
    ['identificacion', 'Información general', 'Identificación, dirección y contacto'],
    ...(proveedor
      ? ([['comercial', 'Compras', 'Condición de pago, flete y clasificación']] as Array<
          [Seccion, string, string]
        >)
      : ([
          ['asignaciones', 'Asignaciones', 'Vendedor, lista, cobrador, documento y visitas'],
          ['credito', 'Crédito', 'Condición de pago, plazo, línea y bloqueo'],
        ] as Array<[Seccion, string, string]>)),
    [
      'direcciones',
      ficha ? `Direcciones (${activas})` : 'Direcciones',
      'Tributaria, despacho y sucursales',
    ],
    [
      'contactos',
      ficha ? `Contactos (${ficha.contactos.length})` : 'Contactos',
      'Personas, correos y teléfonos',
    ],
    [
      'cuentas',
      'Cuentas bancarias',
      proveedor ? 'Adónde se le transfiere' : 'Para identificar sus pagos',
    ],
    ['observaciones', 'Observaciones', 'Bitácora de notas y acuerdos'],
  ];
  const actual = secciones.find((s) => s[0] === seccion)!;
  return (
    <section
      className="ficha-maestro"
      aria-label={proveedor ? 'Ficha del proveedor' : 'Ficha del cliente'}
    >
      <nav className="ficha-nav" role="tablist" aria-label="Secciones de la ficha">
        {secciones.map(([id, n, d]) => {
          // Cliente nuevo: lo que no se llena al crearlo se completa una vez creado (directo con
          // permiso o pidiéndolo).
          const bloqueada = !ficha && !alta?.[id];
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={seccion === id}
              className={seccion === id ? 'activa' : bloqueada ? 'bloqueada' : ''}
              disabled={bloqueada}
              title={
                bloqueada
                  ? 'Se completa una vez creado el cliente: directo con permiso o pidiendo autorización'
                  : undefined
              }
              onClick={() => setSeccion(id)}
            >
              <span className="n">
                {bloqueada ? '🔒 ' : ''}
                {n}
              </span>
              <span className="d">{d}</span>
            </button>
          );
        })}
      </nav>
      <div className="ficha-cuerpo">
        <header className="ficha-cabecera">
          <h2>{actual[1].replace(/ \(\d+\)$/, '')}</h2>
          <p className="apagado">{actual[2]}</p>
        </header>
        {!ficha && alta?.[seccion]}
        {ficha && (
          <>
            {seccion === 'identificacion' && (
              <Identificacion
                empresaId={empresaId}
                ficha={ficha}
                rel={proveedor ? null : rel}
                alIrA={setSeccion}
              />
            )}
            {seccion === 'comercial' && proveedor && rel && (
              <ComercialProveedor
                empresaId={empresaId}
                ficha={ficha}
                rel={rel}
                alSolicitarCambio={alSolicitarCambio}
              />
            )}
            {seccion === 'asignaciones' &&
              (rel ? (
                <Asignaciones
                  empresaId={empresaId}
                  ficha={ficha}
                  rel={rel}
                  vendedores={vendedores}
                  listas={listas}
                />
              ) : (
                <p className="apagado">El cliente no opera con esta empresa.</p>
              ))}
            {seccion === 'credito' &&
              (rel ? (
                <Credito empresaId={empresaId} ficha={ficha} rel={rel} />
              ) : (
                <p className="apagado">El cliente no opera con esta empresa.</p>
              ))}
            {seccion === 'direcciones' && <Direcciones empresaId={empresaId} ficha={ficha} />}
            {seccion === 'contactos' && <Contactos empresaId={empresaId} ficha={ficha} />}
            {seccion === 'cuentas' && (
              <CuentasBancarias empresaId={empresaId} entidadId={ficha.entidad.id} />
            )}
            {seccion === 'observaciones' && (
              <Observaciones empresaId={empresaId} entidadId={ficha.entidad.id} />
            )}
            {seccion === 'comercial' && !rel && (
              <p className="apagado">No opera con esta empresa.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- identificación

function Identificacion({
  empresaId,
  ficha,
  rel,
  alIrA,
}: {
  empresaId: string;
  ficha: FichaEntidad;
  /** La relación de cliente con la empresa (su tipo de entidad); null en la ficha del proveedor. */
  rel: FichaEntidad['empresas'][number] | null;
  alIrA: (s: Seccion) => void;
}) {
  const { puede } = useSesion();
  const e = ficha.entidad;
  const editable = puede('entidades.global.gestionar');
  const guardar = useGuardar(empresaId, e.id);
  const principal =
    ficha.direcciones.find((d) => d.activa && d.es_principal) ??
    ficha.direcciones.find((d) => d.activa && d.es_tributaria) ??
    null;
  const medio = (pred: (m: Medio) => boolean) =>
    ficha.medios.find((m) => m.activo !== false && pred(m))?.valor ?? '';
  const inicial: ValoresGenerales = {
    rut: e.rut ?? '',
    dv: e.dv ?? '',
    razonSocial: e.razon_social,
    nombreFantasia: e.nombre_fantasia ?? '',
    giro: e.giro ?? '',
    tipoEntidad: rel ? (rolDe(rel) as TipoEntidad) : 'proveedor',
    calle: principal?.calle ?? '',
    numero: principal?.numero ?? '',
    comuna: principal?.comuna_codigo ?? '',
    referencia: principal?.referencia ?? '',
    telefono:
      medio((m) => m.tipo === 'telefono' && !!m.principal) || medio((m) => m.tipo === 'telefono'),
    correo:
      medio((m) => m.tipo === 'correo' && m.proposito === 'comercial') ||
      medio((m) => m.tipo === 'correo' && m.proposito !== 'dte'),
    correoDte: medio((m) => m.proposito === 'dte'),
  };
  const [v, setV] = useState(inicial);
  const poner = (c: Partial<ValoresGenerales>) => setV((x) => ({ ...x, ...c }));
  const cambioEntidad =
    v.razonSocial !== inicial.razonSocial ||
    v.nombreFantasia !== inicial.nombreFantasia ||
    v.giro !== inicial.giro;
  const cambioTipo = !!rel && v.tipoEntidad !== inicial.tipoEntidad;
  const guardarTodo = async () => {
    if (cambioEntidad)
      await guardar.mutateAsync({
        metodo: 'patch',
        ruta: `/entidades/${e.id}`,
        cuerpo: {
          version: (e as { version?: number }).version ?? 1,
          razonSocial: v.razonSocial.trim(),
          nombreFantasia: v.nombreFantasia.trim() || null,
          giro: v.giro.trim() || null,
          tipoPersona: e.tipo_persona,
        },
      });
    if (cambioTipo && rel)
      await guardar.mutateAsync({
        metodo: 'patch',
        ruta: `/entidades/${e.id}/comercial`,
        cuerpo: {
          version: rel.version ?? 1,
          esCliente: v.tipoEntidad !== 'proveedor',
          esProveedor: v.tipoEntidad !== 'cliente',
          documentoPorDefecto: rel.documento_por_defecto ?? null,
          costoLogistico: !!rel.costo_logistico,
          diasVisita: rel.dias_visita ?? null,
          cobradorId: rel.cobrador_id ?? null,
          segmento: rel.segmento ?? null,
          clasificacion: rel.clasificacion ?? null,
        },
      });
  };
  return (
    <>
      <InformacionGeneral
        v={v}
        poner={poner}
        alta={false}
        editable={editable}
        estado={
          <>
            {e.estado}
            {e.motivo_estado ? ` · ${e.motivo_estado}` : ''}
          </>
        }
        tiposEntidad={rel ? ['cliente', 'ambos', 'proveedor'] : ['proveedor']}
        alIrA={alIrA}
      />
      {!principal && (
        <p className="texto-aviso">Sin dirección principal: agrégala en Direcciones.</p>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
      {editable && (
        <div className="acciones-ficha">
          <button
            type="button"
            className="primario"
            disabled={
              (!cambioEntidad && !cambioTipo) ||
              guardar.isPending ||
              v.razonSocial.trim().length < 2
            }
            onClick={() => void guardarTodo()}
          >
            Guardar información general
          </button>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------- comercial

type Rol = 'cliente' | 'proveedor' | 'ambos';
/** Cliente, proveedor o ambos, según las marcas de la relación con la empresa. */
const rolDe = (rel: { es_cliente?: boolean; es_proveedor?: boolean }): Rol =>
  rel.es_proveedor ? (rel.es_cliente === false ? 'proveedor' : 'ambos') : 'cliente';

// ---------------------------------------------------------------- compras (proveedor)

const NOMBRE_FLETE: Record<string, string> = {
  FLETE_POR_VALOR: 'En un total, repartido por valor',
  FLETE_POR_UNIDADES: 'En un total, repartido por unidades',
  FLETE_POR_COSTO_LOGISTICO: 'En un total, repartido por costo logístico de cada producto',
};

/** La parte comercial del PROVEEDOR: cómo se le paga, cómo cobra el flete y su clasificación. */
function ComercialProveedor({
  empresaId,
  ficha,
  rel,
  alSolicitarCambio,
}: {
  empresaId: string;
  ficha: FichaEntidad;
  rel: FichaEntidad['empresas'][number];
  alSolicitarCambio?: (() => void) | undefined;
}) {
  const { puede } = useSesion();
  const editable = puede('entidades.gestionar');
  const guardar = useGuardar(empresaId, ficha.entidad.id);
  const flete = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'estrategia-flete', ficha.entidad.id],
    queryFn: () =>
      api.get<{ estrategia: { estrategia: string } | null }>(
        '/compras/costos-logisticos/estrategia',
        { query: { empresaId, proveedorEntidadId: ficha.entidad.id } },
      ),
  });
  const inicial = {
    rol: rolDe(rel),
    segmento: rel.segmento ?? '',
    clasificacion: rel.clasificacion ?? '',
  };
  const [f, setF] = useState(inicial);
  const cambio = (Object.keys(inicial) as Array<keyof typeof inicial>).some(
    (k) => f[k] !== inicial[k],
  );
  const estrategia = flete.data?.estrategia?.estrategia;
  return (
    <div className="ficha-seccion">
      <h3>
        Condiciones de pago{' '}
        <span className="apagado">(se cambian con «Editar / solicitar cambio»)</span>
      </h3>
      <div className="ficha-grilla">
        <Campo etiqueta="Condición de pago">
          {NOMBRE_CONDICION[rel.condicion_pago] ?? rel.condicion_pago}
        </Campo>
        <Campo etiqueta="Plazo" ayuda="Con él se calcula el vencimiento de cada factura.">
          {rel.plazo_dias ? `${rel.plazo_dias} días` : 'Al contado'}
        </Campo>
        <Campo etiqueta="Estado">
          {rel.bloqueado
            ? `BLOQUEADO${rel.motivo_bloqueo ? ` · ${rel.motivo_bloqueo}` : ''}`
            : rel.estado}
        </Campo>
      </div>
      {alSolicitarCambio && (
        <div className="acciones-ficha">
          <button type="button" onClick={alSolicitarCambio}>
            Editar / solicitar cambio…
          </button>
        </div>
      )}
      <h3>Flete</h3>
      <div className="ficha-grilla">
        <Campo
          etiqueta="Cómo cobra el flete"
          ayuda="Se cambia en «Flete y costos logísticos» del resumen del proveedor (o en Compras › Costos logísticos)."
        >
          {estrategia ? (NOMBRE_FLETE[estrategia] ?? estrategia) : 'En la línea de cada producto'}
        </Campo>
      </div>
      <h3>Clasificación</h3>
      <div className="ficha-grilla">
        <Campo
          etiqueta="Tipo de entidad"
          ayuda="Solo los proveedores aparecen en compras y pago a proveedores; solo los clientes en ventas y cobranza."
        >
          <select
            aria-label="Tipo de entidad"
            disabled={!editable}
            value={f.rol}
            onChange={(ev) => setF({ ...f, rol: ev.target.value as Rol })}
          >
            <option value="cliente">Cliente</option>
            <option value="proveedor">Proveedor</option>
            <option value="ambos">Cliente y proveedor</option>
          </select>
        </Campo>
        <Campo etiqueta="Segmento" ayuda="Ej.: bebidas, abarrotes, servicios, transporte.">
          <input
            aria-label="Segmento"
            disabled={!editable}
            maxLength={40}
            value={f.segmento}
            onChange={(ev) => setF({ ...f, segmento: ev.target.value })}
          />
        </Campo>
        <Campo etiqueta="Clasificación" ayuda="Ej.: estratégico, ocasional, relacionado.">
          <input
            aria-label="Clasificación"
            disabled={!editable}
            maxLength={40}
            value={f.clasificacion}
            onChange={(ev) => setF({ ...f, clasificacion: ev.target.value })}
          />
        </Campo>
      </div>
      {guardar.isError && <PanelError error={guardar.error} />}
      {editable && (
        <div className="acciones-ficha">
          <button
            type="button"
            className="primario"
            disabled={!cambio || guardar.isPending}
            onClick={() =>
              guardar.mutate({
                metodo: 'patch',
                ruta: `/entidades/${ficha.entidad.id}/comercial`,
                cuerpo: {
                  version: rel.version ?? 1,
                  esCliente: f.rol !== 'proveedor',
                  esProveedor: f.rol !== 'cliente',
                  documentoPorDefecto: rel.documento_por_defecto ?? null,
                  costoLogistico: !!rel.costo_logistico,
                  diasVisita: rel.dias_visita ?? null,
                  cobradorId: rel.cobrador_id ?? null,
                  segmento: f.segmento.trim() || null,
                  clasificacion: f.clasificacion.trim() || null,
                },
              })
            }
          >
            Guardar clasificación
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- direcciones

function Direcciones({ empresaId, ficha }: { empresaId: string; ficha: FichaEntidad }) {
  const { puede } = useSesion();
  const editable = puede('entidades.global.gestionar');
  const comunas = useComunas();
  const guardar = useGuardar(empresaId, ficha.entidad.id);
  const [editando, setEditando] = useState<Direccion | 'nueva' | null>(null);
  const [verInactivas, setVerInactivas] = useState(false);
  const nombreComuna = (c: string | null) =>
    (c && comunas.data?.find((x) => x.codigo === c)?.nombre) || c || '—';
  const lista = ficha.direcciones.filter((d) => verInactivas || d.activa);
  return (
    <div className="ficha-seccion">
      <table className="grilla compacta" aria-label="Direcciones del cliente">
        <thead>
          <tr>
            <th>Nombre / sucursal</th>
            <th>Dirección</th>
            <th>Comuna</th>
            <th>Ciudad</th>
            <th>Uso</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lista.map((d) => (
            <tr key={d.id} className={d.activa ? '' : 'apagado'}>
              <td>{d.nombre ?? '—'}</td>
              <td>
                {[d.calle, d.numero, d.complemento].filter(Boolean).join(' ')}
                {d.referencia ? <div className="apagado">{d.referencia}</div> : null}
              </td>
              <td>{nombreComuna(d.comuna_codigo)}</td>
              <td>{d.ciudad ?? ''}</td>
              <td>
                {[
                  d.es_principal && 'principal',
                  d.es_tributaria && 'tributaria',
                  d.es_despacho && 'despacho',
                  !d.activa && 'inactiva',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </td>
              <td className="acciones-fila">
                {editable && (
                  <>
                    <button type="button" className="boton-mini" onClick={() => setEditando(d)}>
                      Editar
                    </button>
                    <button
                      type="button"
                      className="boton-mini"
                      disabled={guardar.isPending}
                      onClick={() =>
                        guardar.mutate({
                          metodo: 'patch',
                          ruta: `/entidades/direcciones/${d.id}`,
                          cuerpo: { version: d.version ?? 1, activa: !d.activa },
                        })
                      }
                    >
                      {d.activa ? 'Desactivar' : 'Reactivar'}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!ficha.direcciones.some((d) => d.activa && d.es_tributaria) && (
        <p className="tono-aviso">
          Sin dirección tributaria: la factura no se puede emitir. Marca una como tributaria.
        </p>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
      <div className="acciones-ficha">
        <label>
          <input
            type="checkbox"
            checked={verInactivas}
            onChange={(e) => setVerInactivas(e.target.checked)}
          />{' '}
          Ver inactivas
        </label>
        {editable && (
          <button type="button" className="primario" onClick={() => setEditando('nueva')}>
            Agregar dirección
          </button>
        )}
      </div>
      {editando && (
        <DialogoDireccion
          empresaId={empresaId}
          entidadId={ficha.entidad.id}
          direccion={editando === 'nueva' ? null : editando}
          alCerrar={() => setEditando(null)}
        />
      )}
    </div>
  );
}

function DialogoDireccion({
  empresaId,
  entidadId,
  direccion,
  alCerrar,
}: {
  empresaId: string;
  entidadId: string;
  direccion: Direccion | null;
  alCerrar: () => void;
}) {
  const comunas = useComunas();
  const guardar = useGuardar(empresaId, entidadId);
  const [f, setF] = useState({
    nombre: direccion?.nombre ?? '',
    calle: direccion?.calle ?? '',
    numero: direccion?.numero ?? '',
    complemento: direccion?.complemento ?? '',
    comuna: direccion?.comuna_codigo ?? '',
    ciudad: direccion?.ciudad ?? '',
    referencia: direccion?.referencia ?? '',
    principal: direccion?.es_principal ?? false,
    tributaria: direccion?.es_tributaria ?? false,
    despacho: direccion?.es_despacho ?? true,
  });
  const texto = (v: string) => v.trim() || (direccion ? null : undefined);
  const enviar = () => {
    const comun = {
      nombre: texto(f.nombre),
      numero: texto(f.numero),
      complemento: texto(f.complemento),
      ciudad: texto(f.ciudad),
      referencia: texto(f.referencia),
      calle: f.calle.trim(),
      comunaCodigo: f.comuna || (direccion ? null : undefined),
      esPrincipal: f.principal,
      esTributaria: f.tributaria,
      esDespacho: f.despacho,
    };
    const limpio = Object.fromEntries(Object.entries(comun).filter(([, v]) => v !== undefined));
    guardar.mutate(
      direccion
        ? {
            metodo: 'patch',
            ruta: `/entidades/direcciones/${direccion.id}`,
            cuerpo: { version: direccion.version ?? 1, ...limpio },
          }
        : { metodo: 'post', ruta: `/entidades/${entidadId}/direcciones`, cuerpo: limpio },
      { onSuccess: alCerrar },
    );
  };
  const campo = (
    k: 'nombre' | 'calle' | 'numero' | 'complemento' | 'ciudad' | 'referencia',
    n: string,
  ) => (
    <label className="campo">
      <span className="etiqueta">{n}</span>
      <input aria-label={n} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );
  return (
    <Modal titulo={direccion ? 'Editar dirección' : 'Agregar dirección'} alCerrar={alCerrar}>
      {campo('nombre', 'Nombre / sucursal')}
      {campo('calle', 'Calle')}
      <div className="ficha-grilla">
        {campo('numero', 'Número')}
        {campo('complemento', 'Depto / local')}
      </div>
      <div className="ficha-grilla">
        <label className="campo">
          <span className="etiqueta">Comuna</span>
          <select
            aria-label="Comuna"
            value={f.comuna}
            onChange={(e) => setF({ ...f, comuna: e.target.value })}
          >
            <option value="">Comuna…</option>
            {(comunas.data ?? []).map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.nombre}
              </option>
            ))}
          </select>
        </label>
        {campo('ciudad', 'Ciudad')}
      </div>
      {campo('referencia', 'Referencia (horario, cómo llegar)')}
      <div className="marcas-direccion">
        {(
          [
            ['principal', 'Principal'],
            ['tributaria', 'Tributaria (la de la factura)'],
            ['despacho', 'Despacho'],
          ] as const
        ).map(([k, n]) => (
          <label key={k}>
            <input
              type="checkbox"
              checked={f[k]}
              onChange={(e) => setF({ ...f, [k]: e.target.checked })}
            />{' '}
            {n}
          </label>
        ))}
      </div>
      {guardar.isError && <PanelError error={guardar.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={guardar.isPending || f.calle.trim().length < 2}
          onClick={enviar}
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- contactos y medios

function Contactos({ empresaId, ficha }: { empresaId: string; ficha: FichaEntidad }) {
  const { puede } = useSesion();
  const editable = puede('entidades.global.gestionar');
  const guardar = useGuardar(empresaId, ficha.entidad.id);
  const [contacto, setContacto] = useState<Contacto | 'nuevo' | null>(null);
  const [medio, setMedio] = useState<Medio | 'nuevo' | null>(null);
  const nombreContacto = (id: string | null) =>
    id ? (ficha.contactos.find((c) => c.id === id)?.nombre ?? '—') : 'Empresa';
  const alternar = (ruta: string, version: number | undefined, campo: string, valor: boolean) =>
    guardar.mutate({ metodo: 'patch', ruta, cuerpo: { version: version ?? 1, [campo]: valor } });
  return (
    <div className="ficha-seccion">
      <h3>Correos y teléfonos</h3>
      <table className="grilla compacta" aria-label="Medios de contacto">
        <thead>
          <tr>
            <th>Tipo</th>
            <th>Para</th>
            <th>Valor</th>
            <th>De</th>
            <th>Principal</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {ficha.medios.map((m) => (
            <tr key={m.id} className={m.activo === false ? 'apagado' : ''}>
              <td>{m.tipo}</td>
              <td>{NOMBRE_PROPOSITO[m.proposito] ?? m.proposito}</td>
              <td className="mono">{m.valor}</td>
              <td>{nombreContacto(m.contacto_id)}</td>
              <td>{m.principal ? 'sí' : ''}</td>
              <td className="acciones-fila">
                {editable && (
                  <>
                    <button type="button" className="boton-mini" onClick={() => setMedio(m)}>
                      Editar
                    </button>
                    <button
                      type="button"
                      className="boton-mini"
                      disabled={guardar.isPending}
                      onClick={() =>
                        alternar(
                          `/entidades/medios/${m.id}`,
                          m.version,
                          'activo',
                          m.activo === false,
                        )
                      }
                    >
                      {m.activo === false ? 'Reactivar' : 'Desactivar'}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!ficha.medios.some(
        (m) => m.activo !== false && m.proposito === 'dte' && m.tipo === 'correo',
      ) && <p className="apagado">Sin correo de factura electrónica (DTE).</p>}
      {editable && (
        <div className="acciones-ficha">
          <button type="button" onClick={() => setMedio('nuevo')}>
            Agregar correo o teléfono
          </button>
        </div>
      )}
      <h3>Personas de contacto</h3>
      <table className="grilla compacta" aria-label="Contactos del cliente">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Cargo</th>
            <th>Área</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {ficha.contactos.map((c) => (
            <tr key={c.id} className={c.activo ? '' : 'apagado'}>
              <td>{c.nombre}</td>
              <td>{c.cargo ?? ''}</td>
              <td>{c.area}</td>
              <td className="acciones-fila">
                {editable && (
                  <>
                    <button type="button" className="boton-mini" onClick={() => setContacto(c)}>
                      Editar
                    </button>
                    <button
                      type="button"
                      className="boton-mini"
                      disabled={guardar.isPending}
                      onClick={() =>
                        alternar(`/entidades/contactos/${c.id}`, c.version, 'activo', !c.activo)
                      }
                    >
                      {c.activo ? 'Desactivar' : 'Reactivar'}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {guardar.isError && <PanelError error={guardar.error} />}
      {editable && (
        <div className="acciones-ficha">
          <button type="button" onClick={() => setContacto('nuevo')}>
            Agregar contacto
          </button>
        </div>
      )}
      {contacto && (
        <DialogoContacto
          empresaId={empresaId}
          entidadId={ficha.entidad.id}
          contacto={contacto === 'nuevo' ? null : contacto}
          alCerrar={() => setContacto(null)}
        />
      )}
      {medio && (
        <DialogoMedio
          empresaId={empresaId}
          ficha={ficha}
          medio={medio === 'nuevo' ? null : medio}
          alCerrar={() => setMedio(null)}
        />
      )}
    </div>
  );
}

function DialogoContacto({
  empresaId,
  entidadId,
  contacto,
  alCerrar,
}: {
  empresaId: string;
  entidadId: string;
  contacto: Contacto | null;
  alCerrar: () => void;
}) {
  const guardar = useGuardar(empresaId, entidadId);
  const [f, setF] = useState({
    nombre: contacto?.nombre ?? '',
    cargo: contacto?.cargo ?? '',
    area: contacto?.area ?? 'comercial',
  });
  return (
    <Modal titulo={contacto ? 'Editar contacto' : 'Agregar contacto'} alCerrar={alCerrar}>
      <label className="campo">
        <span className="etiqueta">Nombre</span>
        <input
          aria-label="Nombre del contacto"
          autoFocus
          value={f.nombre}
          onChange={(e) => setF({ ...f, nombre: e.target.value })}
        />
      </label>
      <label className="campo">
        <span className="etiqueta">Cargo</span>
        <input
          aria-label="Cargo"
          value={f.cargo}
          onChange={(e) => setF({ ...f, cargo: e.target.value })}
        />
      </label>
      <label className="campo">
        <span className="etiqueta">Área</span>
        <select
          aria-label="Área"
          value={f.area}
          onChange={(e) => setF({ ...f, area: e.target.value })}
        >
          {AREAS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </label>
      {guardar.isError && <PanelError error={guardar.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={guardar.isPending || f.nombre.trim().length < 2}
          onClick={() =>
            guardar.mutate(
              contacto
                ? {
                    metodo: 'patch',
                    ruta: `/entidades/contactos/${contacto.id}`,
                    cuerpo: {
                      version: contacto.version ?? 1,
                      nombre: f.nombre.trim(),
                      cargo: f.cargo.trim() || null,
                      area: f.area,
                    },
                  }
                : {
                    metodo: 'post',
                    ruta: `/entidades/${entidadId}/contactos`,
                    cuerpo: {
                      nombre: f.nombre.trim(),
                      ...(f.cargo.trim() ? { cargo: f.cargo.trim() } : {}),
                      area: f.area,
                    },
                  },
              { onSuccess: alCerrar },
            )
          }
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

function DialogoMedio({
  empresaId,
  ficha,
  medio,
  alCerrar,
}: {
  empresaId: string;
  ficha: FichaEntidad;
  medio: Medio | null;
  alCerrar: () => void;
}) {
  const guardar = useGuardar(empresaId, ficha.entidad.id);
  const [f, setF] = useState({
    tipo: medio?.tipo ?? 'correo',
    proposito: medio?.proposito ?? 'comercial',
    valor: medio?.valor ?? '',
    contactoId: medio?.contacto_id ?? '',
    principal: medio?.principal ?? true,
  });
  return (
    <Modal
      titulo={medio ? 'Editar correo o teléfono' : 'Agregar correo o teléfono'}
      alCerrar={alCerrar}
    >
      <div className="ficha-grilla">
        <label className="campo">
          <span className="etiqueta">Tipo</span>
          <select
            aria-label="Tipo"
            disabled={!!medio}
            value={f.tipo}
            onChange={(e) => setF({ ...f, tipo: e.target.value })}
          >
            {TIPOS_MEDIO.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Para</span>
          <select
            aria-label="Para qué"
            value={f.proposito}
            onChange={(e) => setF({ ...f, proposito: e.target.value })}
          >
            {PROPOSITOS.map((p) => (
              <option key={p} value={p}>
                {NOMBRE_PROPOSITO[p]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="campo">
        <span className="etiqueta">{f.tipo === 'correo' ? 'Correo' : 'Número / dirección'}</span>
        <input
          aria-label="Valor"
          autoFocus
          value={f.valor}
          onChange={(e) => setF({ ...f, valor: e.target.value })}
        />
      </label>
      {!medio && (
        <label className="campo">
          <span className="etiqueta">De</span>
          <select
            aria-label="De quién"
            value={f.contactoId}
            onChange={(e) => setF({ ...f, contactoId: e.target.value })}
          >
            <option value="">La empresa</option>
            {ficha.contactos
              .filter((c) => c.activo)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
          </select>
        </label>
      )}
      <label>
        <input
          type="checkbox"
          checked={f.principal}
          onChange={(e) => setF({ ...f, principal: e.target.checked })}
        />{' '}
        Principal para ese uso
      </label>
      {guardar.isError && <PanelError error={guardar.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={guardar.isPending || f.valor.trim().length < 3}
          onClick={() =>
            guardar.mutate(
              medio
                ? {
                    metodo: 'patch',
                    ruta: `/entidades/medios/${medio.id}`,
                    cuerpo: {
                      version: medio.version ?? 1,
                      valor: f.valor.trim(),
                      proposito: f.proposito,
                      principal: f.principal,
                    },
                  }
                : {
                    metodo: 'post',
                    ruta: `/entidades/${ficha.entidad.id}/medios`,
                    cuerpo: {
                      tipo: f.tipo,
                      proposito: f.proposito,
                      valor: f.valor.trim(),
                      principal: f.principal,
                      ...(f.contactoId ? { contactoId: f.contactoId } : {}),
                    },
                  },
              { onSuccess: alCerrar },
            )
          }
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- observaciones

function Observaciones({ empresaId, entidadId }: { empresaId: string; entidadId: string }) {
  const { puede } = useSesion();
  const lista = useQuery({
    queryKey: ['emp', empresaId, 'observaciones', entidadId],
    queryFn: () =>
      api.get<Observacion[]>(`/entidades/${entidadId}/observaciones`, { query: { empresaId } }),
  });
  const guardar = useGuardar(empresaId, entidadId);
  const [texto, setTexto] = useState('');
  return (
    <div className="ficha-seccion">
      {puede('entidades.ver') && (
        <div className="nueva-observacion">
          <textarea
            aria-label="Nueva observación"
            placeholder="Horario de recepción, cómo llegar, acuerdos con el cliente…"
            rows={3}
            maxLength={2000}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <button
            type="button"
            className="primario"
            disabled={guardar.isPending || !texto.trim()}
            onClick={() =>
              guardar.mutate(
                {
                  metodo: 'post',
                  ruta: `/entidades/${entidadId}/observaciones`,
                  cuerpo: { texto: texto.trim() },
                },
                { onSuccess: () => setTexto('') },
              )
            }
          >
            Agregar observación
          </button>
        </div>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
      {lista.isError && <PanelError error={lista.error} />}
      <ul className="bitacora" aria-label="Observaciones del cliente">
        {(lista.data ?? []).map((o) => (
          <li key={o.id}>
            <span className="apagado">
              {fechaHora(o.registradoEn)} · {o.autor ?? '—'}
            </span>
            <p>{o.texto}</p>
          </li>
        ))}
        {lista.data?.length === 0 && <li className="apagado">Sin observaciones.</li>}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------- cuentas bancarias (2026-10-19)
// Las cuentas del cliente o proveedor: a cuál se le transfiere al pagarle (la principal se propone
// sola en «Pagar») y de cuál suele pagar el cliente. Se desactivan, no se borran.

function CuentasBancarias({ empresaId, entidadId }: { empresaId: string; entidadId: string }) {
  const { puede } = useSesion();
  const editable = puede('entidades.global.gestionar');
  const qc = useQueryClient();
  const clave = ['emp', empresaId, 'cuentas-entidad', entidadId];
  const lista = useQuery({
    queryKey: clave,
    queryFn: () =>
      api.get<CuentaEntidad[]>(`/entidades/${entidadId}/cuentas-bancarias`, {
        query: { empresaId },
      }),
  });
  const guardar = useMutation({
    mutationFn: ({
      metodo,
      ruta,
      cuerpo,
    }: {
      metodo: 'post' | 'patch';
      ruta: string;
      cuerpo: object;
    }) => api[metodo](ruta, { empresaId, ...cuerpo }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: clave }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'pagos', 'destinos', entidadId] }),
      ]),
  });
  const vacia = {
    banco: '',
    tipoCuenta: 'CORRIENTE' as CuentaEntidad['tipoCuenta'],
    numero: '',
    titularRut: '',
    titularNombre: '',
    correo: '',
  };
  const [nueva, setNueva] = useState(vacia);
  const poner = (c: Partial<typeof vacia>) => setNueva((n) => ({ ...n, ...c }));
  const cambiar = (c: CuentaEntidad, cuerpo: object) =>
    guardar.mutate({
      metodo: 'patch',
      ruta: `/entidades/cuentas-bancarias/${c.id}`,
      cuerpo: { version: c.version, ...cuerpo },
    });
  return (
    <div className="ficha-seccion">
      <table className="grilla compacta" aria-label="Cuentas bancarias">
        <thead>
          <tr>
            <th>Banco</th>
            <th>Tipo</th>
            <th>N° de cuenta</th>
            <th>Titular</th>
            <th>Correo</th>
            <th>Principal</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(lista.data ?? []).map((c) => (
            <tr key={c.id} className={c.activa ? '' : 'apagado'}>
              <td>{c.banco}</td>
              <td>{NOMBRE_TIPO_CUENTA[c.tipoCuenta] ?? c.tipoCuenta}</td>
              <td className="mono">{c.numero}</td>
              <td>
                {c.titularRut ? rut(c.titularRut.split('-')[0]!, c.titularRut.split('-')[1]!) : ''}{' '}
                {c.titularNombre ?? ''}
              </td>
              <td>{c.correo ?? ''}</td>
              <td>{c.principal ? 'sí' : ''}</td>
              <td className="acciones-fila">
                {editable && (
                  <>
                    {c.activa && !c.principal && (
                      <button
                        type="button"
                        className="boton-mini"
                        disabled={guardar.isPending}
                        onClick={() => cambiar(c, { principal: true })}
                      >
                        Dejar principal
                      </button>
                    )}
                    <button
                      type="button"
                      className="boton-mini"
                      disabled={guardar.isPending}
                      onClick={() => cambiar(c, { activa: !c.activa })}
                    >
                      {c.activa ? 'Desactivar' : 'Reactivar'}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {lista.isSuccess && !lista.data.length && (
            <tr>
              <td colSpan={7} className="apagado">
                Sin cuentas bancarias registradas.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {editable && (
        <div className="barra-filtros datos-pago">
          <label className="campo">
            <span className="etiqueta">Banco</span>
            <SelectorBanco
              etiqueta="Banco de la cuenta"
              valor={nueva.banco}
              alCambiar={(v) => poner({ banco: v })}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Tipo</span>
            <select
              aria-label="Tipo de cuenta"
              value={nueva.tipoCuenta}
              onChange={(e) => poner({ tipoCuenta: e.target.value as CuentaEntidad['tipoCuenta'] })}
            >
              {(['CORRIENTE', 'VISTA', 'AHORRO', 'RUT'] as const).map((t) => (
                <option key={t} value={t}>
                  {NOMBRE_TIPO_CUENTA[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta">N° de cuenta</span>
            <input
              aria-label="Número de cuenta"
              value={nueva.numero}
              onChange={(e) => poner({ numero: e.target.value })}
            />
          </label>
          <label className="campo campo-corto">
            <span className="etiqueta">RUT titular</span>
            <input
              aria-label="RUT del titular"
              placeholder="opcional"
              value={nueva.titularRut}
              onChange={(e) => poner({ titularRut: e.target.value })}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Nombre titular</span>
            <input
              aria-label="Nombre del titular"
              placeholder="opcional"
              value={nueva.titularNombre}
              onChange={(e) => poner({ titularNombre: e.target.value })}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Correo aviso</span>
            <input
              aria-label="Correo de aviso"
              type="email"
              placeholder="opcional"
              value={nueva.correo}
              onChange={(e) => poner({ correo: e.target.value })}
            />
          </label>
          <button
            type="button"
            className="primario"
            disabled={
              guardar.isPending || nueva.banco.trim().length < 2 || nueva.numero.trim().length < 3
            }
            onClick={() =>
              guardar.mutate(
                {
                  metodo: 'post',
                  ruta: `/entidades/${entidadId}/cuentas-bancarias`,
                  cuerpo: {
                    banco: nueva.banco.trim(),
                    tipoCuenta: nueva.tipoCuenta,
                    numero: nueva.numero.trim(),
                    titularRut: nueva.titularRut.trim() || null,
                    titularNombre: nueva.titularNombre.trim() || null,
                    correo: nueva.correo.trim() || null,
                  },
                },
                { onSuccess: () => setNueva(vacia) },
              )
            }
          >
            Agregar cuenta
          </button>
        </div>
      )}
      {guardar.isError && <PanelError error={guardar.error} />}
      {lista.isError && <PanelError error={lista.error} />}
    </div>
  );
}

function Campo({
  etiqueta,
  ayuda,
  children,
}: {
  etiqueta: string;
  ayuda?: string;
  children: ReactNode;
}) {
  return (
    <div className="campo">
      <span className="etiqueta">{etiqueta}</span>
      {children}
      {ayuda && <span className="ayuda">{ayuda}</span>}
    </div>
  );
}
